import { FilterModel } from 'ag-grid-community';
import { FormikErrors } from 'formik';

import { FilterModelParameters } from 'app/models';

import { formatMessage } from 'utils/messages/utils';

import { Attribute, AttributeType } from './useAccountAttributes';

export const validateAttributeFilterField = (
  attributes: Attribute[],
  clauses: ClauseValue[]
): FormikErrors<ClauseValue[]> | null => {
  const typeLookup = createTypeLookup(attributes);

  const clauseErrors = clauses.map((clause, index) => {
    const type = typeLookup.get(clause.column);
    if (!type) {
      return { column: formatMessage('ATTRIBUTE_ERROR_INVALID_COLUMN') };
    }
    const validOperators = operatorsByType[type];
    if (!validOperators || validOperators.every((validOperator) => validOperator.value !== clause.operator)) {
      return { operator: formatMessage('ATTRIBUTE_ERROR_INVALID_OPERATOR') };
    }

    const otherIndex = clauses.findIndex((otherClause) => otherClause.column === clause.column);
    if (otherIndex !== index) {
      return { column: formatMessage('ATTRIBUTE_ERROR_DUPLICATE_COLUMN') };
    }

    const parameterErrors = validateParameters(type, clause.operator, clause.parameters);
    if (parameterErrors) {
      return { parameters: parameterErrors };
    }

    return null;
  });

  if (clauseErrors.length > 0 && clauseErrors.some(Boolean)) return clauseErrors;
  return null;
};

type TextFilterOperators = 'equals' | 'notEqual' | 'contains' | 'notContains' | 'startsWith' | 'endsWith';
type NumberFilterOperators =
  | 'equals'
  | 'notEqual'
  | 'lessThan'
  | 'lessThanOrEqual'
  | 'greaterThan'
  | 'greaterThanOrEqual';
type DateFilterOperators = 'equals' | 'notEqual' | 'lessThan' | 'greaterThan' | 'inRange';

export type AttributeFilterOperator = TextFilterOperators | NumberFilterOperators | DateFilterOperators;

export const defaultOperator: TextFilterOperators & DateFilterOperators & NumberFilterOperators = 'equals';

export const defaultParametersByType: Record<AttributeType, FilterModelParameters> = {
  string: { filter: '', filterTo: '' },
  number: { filter: '', filterTo: '' },
  date: { dateFrom: '', dateTo: '' }
};

export const operatorsByType: Record<AttributeType, { text: string; value: AttributeFilterOperator }[]> = {
  string: [
    {
      text: formatMessage('EQUALS'),
      value: 'equals'
    },
    {
      text: formatMessage('NOT_EQUAL'),
      value: 'notEqual'
    },
    {
      text: formatMessage('CONTAINS'),
      value: 'contains'
    },
    {
      text: formatMessage('NOT_CONTAINS'),
      value: 'notContains'
    },
    {
      text: formatMessage('STARTS_WITH'),
      value: 'startsWith'
    },
    {
      text: formatMessage('ENDS_WITH'),
      value: 'endsWith'
    }
  ],
  number: [
    {
      text: formatMessage('EQUALS'),
      value: 'equals'
    },
    {
      text: formatMessage('NOT_EQUAL'),
      value: 'notEqual'
    },
    {
      text: formatMessage('IN_RANGE'),
      value: 'inRange'
    },
    {
      text: formatMessage('GREATER_THAN'),
      value: 'greaterThan'
    },
    {
      text: formatMessage('GREATER_THAN_OR_EQUAL'),
      value: 'greaterThanOrEqual'
    },
    {
      text: formatMessage('LESS_THAN'),
      value: 'lessThan'
    },
    {
      text: formatMessage('LESS_THAN_OR_EQUAL'),
      value: 'lessThanOrEqual'
    }
  ],
  date: [
    {
      text: formatMessage('EQUALS'),
      value: 'equals'
    },
    {
      text: formatMessage('NOT_EQUAL'),
      value: 'notEqual'
    },
    {
      text: formatMessage('IN_RANGE'),
      value: 'inRange'
    },
    {
      text: formatMessage('GREATER_THAN'),
      value: 'greaterThan'
    },
    {
      text: formatMessage('GREATER_THAN_OR_EQUAL'),
      value: 'greaterThanOrEqual'
    },
    {
      text: formatMessage('LESS_THAN'),
      value: 'lessThan'
    },
    {
      text: formatMessage('LESS_THAN_OR_EQUAL'),
      value: 'lessThanOrEqual'
    }
  ]
};

const getParameterKeys = (type: AttributeType) => (type === 'date' ? ['dateFrom', 'dateTo'] : ['filter', 'filterTo']);

const validateParameters = (
  type: AttributeType,
  operator: AttributeFilterOperator,
  parameters: FilterModelParameters
): FormikErrors<FilterModelParameters> => {
  const [firstKey, secondKey] = getParameterKeys(type);

  const firstKeyError = validateParameterValue(type, parameters[firstKey] ?? '');
  if (firstKeyError) return { [firstKey]: firstKeyError };

  if (operator !== 'inRange') return undefined;

  const secondKeyError = validateParameterValue(type, parameters[secondKey] ?? '');
  if (secondKeyError) return { [secondKey]: secondKeyError };

  return undefined;
};

const validateParameterValue = (type: AttributeType, value: string) => {
  if (value.toString().trim() === '') return formatMessage('ATTRIBUTE_ERROR_PARAMETER_REQUIRED');
  if (type === 'number' && isNaN(+value)) return formatMessage('ATTRIBUTE_ERROR_PARAMETER_INVALID_NUMBER');
  if (type === 'date' && (!value || isNaN(new Date(value).valueOf())))
    return formatMessage('ATTRIBUTE_ERROR_PARAMETER_INVALID_DATE');
  return undefined;
};

export const createTypeLookup = (attributes: Attribute[]) =>
  new Map(attributes.map((attribute) => [attribute.column, attribute.type]));

export const formStateToFilterModel = (attributes: Attribute[], clauses: ClauseValue[]): FilterModel => {
  const typeLookup = createTypeLookup(attributes);
  const filterModel: FilterModel = {};
  for (const clause of clauses) {
    const type = typeLookup.get(clause.column);
    const [firstParamKey, secondParamKey] = getParameterKeys(type);

    const firstValue = asAttributeTypeForFilterModel(clause.parameters[firstParamKey], type);
    const secondValue = asAttributeTypeForFilterModel(clause.parameters[secondParamKey], type);

    filterModel[clause.column] = {
      filterType: typeAsFilterType(type),
      type: clause.operator,
      [firstParamKey]: firstValue
    };
    if (clause.operator === 'inRange') {
      filterModel[clause.column][firstParamKey] = firstValue < secondValue ? firstValue : secondValue;
      filterModel[clause.column][secondParamKey] = firstValue < secondValue ? secondValue : firstValue;
    }
  }
  return filterModel;
};

const asAttributeTypeForFilterModel = (value: string | number | Date | null, type: AttributeType) => {
  switch (type) {
    case 'string':
      return (value ?? '').toString();
    case 'number':
      return +value;
    case 'date':
      return new Date(value).toISOString();
  }
};

const typeAsFilterType = (type: AttributeType) => {
  switch (type) {
    case 'string':
      return 'text';
    case 'number':
    case 'date':
      return type;
  }
};

export const filterModelToFormState = (filterModel: FilterModel): ClauseValue[] => {
  if (!filterModel) return [];
  return Object.entries(filterModel).map(([column, { filterType, type, filter, filterTo, dateFrom, dateTo }]) => {
    const value = { column, operator: type, parameters: null };
    if (filterType === 'date') {
      value.parameters = { dateFrom: asDateParameter(dateFrom), dateTo: asDateParameter(dateTo) };
    } else {
      value.parameters = { filter: filter?.toString() ?? '', filterTo: filterTo?.toString() ?? '' };
    }
    return value;
  });
};

const asDateParameter = (rawDate: Date | string | number | null | undefined) => {
  if (!rawDate) return '';
  const date = new Date(rawDate);
  if (isNaN(date.getTime())) return '';
  return date;
};

export type ClauseValue = {
  column: string;
  operator: AttributeFilterOperator;
  parameters: FilterModelParameters;
};
